import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { UserPlus, CheckCircle2 } from 'lucide-react';

const FitnessAdditionalInsured = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <UserPlus className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Additional Insured</CardTitle>
            <CardDescription className="text-white/60">
              Do you need to add any additional insured parties?
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Info Box */}
          <div className="p-4 bg-[#EEF0FF] border border-[#7B6FE8]/20 rounded-lg">
            <p className="text-sm text-[#1A1A1A]">
              <strong>What is an Additional Insured?</strong> An additional insured is a party (such as a landlord, venue, or partner organization) that needs to be named on your insurance policy. This is often required by lease agreements or contracts.
            </p>
          </div>

          {/* Question */}
          <div>
            <Label className="text-base font-semibold">
              Do you need to add any additional insured parties to your policy?
            </Label>
            <p className="text-sm text-slate-600 mb-3">
              Common examples: landlords, fitness equipment lessors, franchise headquarters
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div
                data-testid="additional-insured-no"
                onClick={() => updateFormData({ 
                  additional_insured_required: false, 
                  additional_insured_details: '' 
                })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.additional_insured_required === false
                    ? 'border-slate-500 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${
                    formData.additional_insured_required === false ? 'text-slate-900' : 'text-slate-700'
                  }`}>
                    No, not at this time
                  </span>
                </div>
              </div>

              <div
                data-testid="additional-insured-yes"
                onClick={() => updateFormData({ additional_insured_required: true })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.additional_insured_required === true
                    ? 'border-[#4ECDC4] bg-[#EEF0FF]'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${
                    formData.additional_insured_required === true ? 'text-[#1A1A1A]' : 'text-slate-700'
                  }`}>
                    Yes, I need to add parties
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Insured Details */}
          {formData.additional_insured_required && (
            <div className="space-y-4 p-4 bg-[#EEF0FF] border border-[#4ECDC4]/20 rounded-lg">
              <div>
                <Label htmlFor="additional_insured_details">
                  Additional Insured Details *
                </Label>
                <p className="text-sm text-slate-600 mb-2">
                  Please provide the name, address, and relationship for each additional insured party
                </p>
                <Textarea
                  id="additional_insured_details"
                  data-testid="additional-insured-details"
                  value={formData.additional_insured_details}
                  onChange={(e) => updateFormData({ additional_insured_details: e.target.value })}
                  required={formData.additional_insured_required}
                  className="mt-1.5 min-h-[120px] bg-white"
                  placeholder="Example:&#10;1. ABC Property Management LLC&#10;   123 Main Street, Suite 100, Chicago, IL 60601&#10;   Relationship: Landlord&#10;&#10;2. XYZ Franchise Corp&#10;   456 Corporate Blvd, Denver, CO 80202&#10;   Relationship: Franchisor"
                />
              </div>

              <p className="text-xs text-[#6B7280]">
                Note: Adding additional insureds may affect your premium. Our team will confirm details during the quote process.
              </p>
            </div>
          )}

          {/* No Additional Insured Confirmation */}
          {formData.additional_insured_required === false && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-slate-600" />
                <p className="text-sm text-slate-700">
                  No additional insured parties will be added. You can always request to add them after your policy is issued.
                </p>
              </div>
            </div>
          )}

          <ThemedButton 
            type="submit"
            data-testid="fitness-additional-next-btn"
            disabled={formData.additional_insured_required === undefined || 
              (formData.additional_insured_required && !formData.additional_insured_details)}
            variant="wizard" size="full"
          >
            Next: Review & Submit
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessAdditionalInsured;
